import React from 'react';

const Logo = ({ size = 'default', showText = true, className = '' }) => {
  const sizes = {
    small: { icon: 28, text: 'text-base' },
    default: { icon: 36, text: 'text-lg' },
    large: { icon: 48, text: 'text-2xl' }
  };

  const { icon, text } = sizes[size] || sizes.default;

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      {/* Logo Icon - Robot/AI themed */}
      <svg
        width={icon}
        height={icon}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        {/* Background Circle with Gradient */}
        <defs>
          <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="50%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#3B82F6" />
          </linearGradient>
          <linearGradient id="glowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#60A5FA" />
          </linearGradient>
        </defs>
        
        {/* Outer glow ring */}
        <circle cx="24" cy="24" r="23" stroke="url(#glowGradient)" strokeWidth="1" opacity="0.3" />
        
        {/* Main background */}
        <circle cx="24" cy="24" r="20" fill="url(#logoGradient)" />
        
        {/* Robot Head - Simplified */}
        <rect x="14" y="14" width="20" height="16" rx="4" fill="white" opacity="0.95" />
        
        {/* Eyes */}
        <circle cx="19" cy="21" r="3" fill="#6366F1" />
        <circle cx="29" cy="21" r="3" fill="#6366F1" />
        
        {/* Eye highlights */}
        <circle cx="18" cy="20" r="1" fill="white" />
        <circle cx="28" cy="20" r="1" fill="white" />
        
        {/* Antenna */}
        <line x1="24" y1="14" x2="24" y2="9" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="24" cy="8" r="2" fill="#60A5FA" />
        
        {/* Mouth - Signal waves representing voice/chat */}
        <path d="M18 27 Q24 30 30 27" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" fill="none" />
        
        {/* Bottom connection nodes */}
        <circle cx="18" cy="34" r="2" fill="white" opacity="0.8" />
        <circle cx="24" cy="36" r="2" fill="white" opacity="0.8" />
        <circle cx="30" cy="34" r="2" fill="white" opacity="0.8" />
        
        {/* Connection lines */}
        <line x1="18" y1="30" x2="18" y2="32" stroke="white" strokeWidth="1.5" opacity="0.6" />
        <line x1="24" y1="30" x2="24" y2="34" stroke="white" strokeWidth="1.5" opacity="0.6" />
        <line x1="30" y1="30" x2="30" y2="32" stroke="white" strokeWidth="1.5" opacity="0.6" />
      </svg>

      {/* Text */}
      {showText && (
        <div className="flex flex-col leading-none">
          <span className={`font-bold text-gray-900 tracking-tight ${text}`}>
            AI AutoBots
          </span>
        </div>
      )}
    </div>
  );
};

// Alternative simpler modern logo
export const LogoModern = ({ size = 'default', showText = true, className = '' }) => {
  const sizes = {
    small: { icon: 28, text: 'text-base' },
    default: { icon: 36, text: 'text-lg' },
    large: { icon: 48, text: 'text-2xl' }
  };

  const { icon, text } = sizes[size] || sizes.default;

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <svg
        width={icon}
        height={icon}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        <defs>
          <linearGradient id="modernGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
        </defs>
        
        {/* Hexagon background */}
        <path
          d="M24 4L42 14V34L24 44L6 34V14L24 4Z"
          fill="url(#modernGradient)"
        />
        
        {/* AI Letters stylized */}
        <text
          x="24"
          y="28"
          textAnchor="middle"
          fill="white"
          fontSize="16"
          fontWeight="bold"
          fontFamily="system-ui, sans-serif"
        >
          AI
        </text>
        
        {/* Circuit lines */}
        <circle cx="12" cy="24" r="2" fill="white" opacity="0.5" />
        <circle cx="36" cy="24" r="2" fill="white" opacity="0.5" />
        <line x1="14" y1="24" x2="18" y2="24" stroke="white" strokeWidth="1" opacity="0.5" />
        <line x1="30" y1="24" x2="34" y2="24" stroke="white" strokeWidth="1" opacity="0.5" />
      </svg>

      {showText && (
        <span className={`font-bold text-gray-900 tracking-tight ${text}`}>
          AI AutoBots
        </span>
      )}
    </div>
  );
};

// Sleek minimalist bot logo
export const LogoBot = ({ size = 'default', showText = true, className = '' }) => {
  const sizes = {
    small: { icon: 28, text: 'text-base' },
    default: { icon: 36, text: 'text-lg' },
    large: { icon: 48, text: 'text-2xl' }
  };

  const { icon, text } = sizes[size] || sizes.default;

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <svg
        width={icon}
        height={icon}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        <defs>
          <linearGradient id="botGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#3B82F6" />
          </linearGradient>
        </defs>
        
        {/* Rounded square background */}
        <rect x="4" y="4" width="40" height="40" rx="12" fill="url(#botGradient)" />
        
        {/* Bot face */}
        <rect x="12" y="16" width="24" height="18" rx="4" fill="white" />
        
        {/* Eyes */}
        <rect x="16" y="20" width="5" height="6" rx="2" fill="#6366F1" />
        <rect x="27" y="20" width="5" height="6" rx="2" fill="#6366F1" />
        
        {/* Smile */}
        <path d="M19 29 Q24 32 29 29" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" fill="none" />
        
        {/* Antenna */}
        <rect x="22" y="8" width="4" height="8" rx="2" fill="white" />
        <circle cx="24" cy="8" r="3" fill="#60A5FA" />
      </svg>

      {showText && (
        <span className={`font-bold text-gray-900 tracking-tight ${text}`}>
          AI AutoBots
        </span>
      )}
    </div>
  );
};

export default Logo;
